import { Menu } from 'lucide-react';
import { useMemo } from 'react';

import { IconButton } from '@/components/ui';
import { auditorMediaQueries } from '@/config';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useMenuBar } from '@/services/MenuBarService';

import HeaderContent from './HeaderContent';

interface Props {
  open: boolean;
  title?: React.ReactNode;
  /** Optional trail under/beside the page title (Phase 1 annotation routes). */
  breadcrumb?: React.ReactNode;
  /** Muted description beside the title. */
  subtitle?: React.ReactNode;
  /** Page controls placed before the presence avatars and profile. */
  actions?: React.ReactNode;
  handleDrawerToggle?: () => void;
}

const Header = ({ open, handleDrawerToggle, title, breadcrumb, subtitle, actions }: Props) => {
  const matchDownMD = useMediaQuery(auditorMediaQueries.downLg);
  const phone = useMediaQuery(auditorMediaQueries.downSm);
  const hasChips = useMenuBar(state => Object.keys(state.chips).length > 0);

  const headerContent = useMemo(() => <HeaderContent />, []);
  const showBadge = !open && matchDownMD && hasChips;
  const left = phone ? '0px' : open && !matchDownMD ? 'var(--width-drawer)' : 'var(--width-mini-drawer)';

  return (
    <header
      className="fixed top-0 z-header border-b border-line bg-surface transition-shell duration-300 ease-drawer motion-reduce:transition-none"
      style={{ left, width: `calc(100% - ${left})` }}
    >
      <div className="flex min-h-header items-center gap-2 px-2 py-2 sm:gap-3 sm:px-4">
        <span className="relative">
          <IconButton
            label={open ? 'Close navigation' : 'Open navigation'}
            onClick={handleDrawerToggle}
            variant="ghost"
            className="bg-raised text-ink hover:bg-raised/80"
          >
            <Menu size={18} strokeWidth={2} />
          </IconButton>
          {showBadge && (
            <span
              aria-hidden="true"
              className="absolute right-0 top-0 size-2 rounded-full bg-critical ring-2 ring-surface"
            />
          )}
        </span>
        {(title || breadcrumb || actions)
        // The title truncates first; if page actions still don't fit on a phone, they scroll here
        // rather than pushing the profile button off-screen.
        && (
          <div className="-my-1 flex min-w-0 items-center gap-2 overflow-x-auto py-1 sm:my-0 sm:gap-3 sm:overflow-visible sm:py-0">
            {(title || breadcrumb) && (
              <div className="flex min-w-0 flex-col gap-0.5 overflow-hidden whitespace-nowrap max-sm:**:flex-nowrap sm:overflow-visible sm:whitespace-normal">
                {breadcrumb}
                {title && (
                  <div className="flex min-w-0 items-baseline gap-4">
                    <h1 className="m-0 truncate text-xl font-semibold tracking-title text-ink">{title}</h1>
                    {subtitle && <p className="m-0 hidden truncate text-sm text-ink-muted md:block">{subtitle}</p>}
                  </div>
                )}
              </div>
            )}
            {actions && <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>}
          </div>
        )}
        {headerContent}
      </div>
    </header>
  );
};

export default Header;
